import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  FormControlLabel,
  Switch
} from '@mui/material';
import { TenantConfig } from '../tenant-config';
import MarketDataCard from './MarketDataCard';
import { parseTransactions } from '../utils/transactionParser';

interface TransactionInputProps {
  tenantConfig: TenantConfig;
  onSubmit: (transactions: Transaction[]) => Promise<void>;
  isLoading: boolean;
}

interface Transaction {
  date: string;
  title: string;
  category: string;
  type: 'income' | 'expense';
  amount: number;
  rowIndex: number;
}

export type TransactionData = Transaction;

const TransactionInput: React.FC<TransactionInputProps> = ({
  tenantConfig,
  onSubmit,
  isLoading
}) => {
  const [type, setType] = useState<'income' | 'expense'>('expense');
  const [inputText, setInputText] = useState('');
  const [error, setError] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [useAI, setUseAI] = useState(false); // Default: không dùng AI
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [yesterdayData, setYesterdayData] = useState<Transaction[]>([]);
  const [lastMonthSameDayData, setLastMonthSameDayData] = useState<Transaction[]>([]);

  // Fetch transactions when component mounts
  const fetchTransactions = useCallback(async () => {
    try {
      setIsLoadingTransactions(true);
      const range = `${tenantConfig.sheetName}!A:F`;
      const url = `https://sheets.googleapis.com/v4/spreadsheets/1LbMSWz41ukFdbAZFv1q33XgDqwZKFEixBujI77fbwko/values/${range}?key=${tenantConfig.googleApiKey}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.values && data.values.length > 1) {
        const parsedTransactions: Transaction[] = data.values.slice(1).map((row: any[], index: number) => ({
          date: row[0] || '',
          title: row[1] || '',
          category: row[2] || '',
          type: (row[3] || '').toLowerCase() === 'income' ? 'income' : 'expense',
          amount: parseFloat(row[4]) || 0,
          rowIndex: index + 2
        })).filter((t: Transaction) => t.date && t.title);

        parsedTransactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setTransactions(parsedTransactions);

        // Get yesterday and last month same day data
        const yesterday = getYesterdayTransactions(parsedTransactions);
        const lastMonthSameDay = getLastMonthSameDayTransactions(parsedTransactions);

        setYesterdayData(yesterday);
        setLastMonthSameDayData(lastMonthSameDay);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setIsLoadingTransactions(false);
    }
  }, [tenantConfig.sheetName, tenantConfig.googleApiKey]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const getYesterdayTransactions = (allTransactions: Transaction[]) => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    return allTransactions.filter(t => t.date === yesterdayStr);
  };

  const getLastMonthSameDayTransactions = (allTransactions: Transaction[]) => {
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
    const lastMonthStr = lastMonth.toISOString().split('T')[0];

    return allTransactions.filter(t => t.date === lastMonthStr);
  };

  const handleRecommendationClick = (transaction: Transaction) => {
    const transactionText = `${transaction.title} ${Math.round(transaction.amount / 1000)}k`;
    const currentText = inputText.trim();
    const newText = currentText ? `${currentText}\n${transactionText}` : transactionText;
    setInputText(newText);
  };

  const getRecommendations = () => {
    const recommendations = [];
    // If we have transactions today, prioritize yesterday's data
    if (yesterdayData.length > 0) {
      recommendations.push(...yesterdayData);
    }

    // Otherwise use last month same day data
    if (lastMonthSameDayData.length > 0) {
      recommendations.push(...lastMonthSameDayData);
    }
    return recommendations;
  };

  const analyzeWithGemini = async (text: string) => {
    try {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      const currentDate = new Date().getDate();
      const today = `${currentYear}-${currentMonth.toString().padStart(2, '0')}-${currentDate.toString().padStart(2, '0')}`;

      const prompt = [
        `Phân tích thông tin ${type === 'income' ? 'thu nhập' : 'chi tiêu'} sau và trả về dạng JSON array với các trường: date (định dạng YYYY-MM-DD, sử dụng ngày hiện tại ${today}), title (tiêu đề), category (danh mục), amount (số tiền).`,
        '',
        `Lưu ý về đơn vị tiền tệ:`,
        `- Nếu có "k", "nghìn", "vnd", "đồng" thì chuyển thành số nguyên (VD: "50k" = 50000, "200 nghìn" = 200000)`,
        `- Nếu chỉ có số đơn giản (VD: "200", "50", "1000") thì hiểu là nghìn đồng (VD: "200" = 200000, "50" = 50000)`,
        `- Nếu có số lớn (>= 10000) thì giữ nguyên (VD: "50000" = 50000)`,
        `- Luôn trả về amount là số nguyên, không có dấu phẩy hay ký tự đặc biệt`,
        '',
        `Phân tích category:`,
        `- Phân tích nội dung để xác định danh mục phù hợp nhất`,
        `- Cho ${type === 'income' ? 'thu nhập' : 'chi tiêu'}, hãy phân tích ngữ cảnh để đặt category chính xác`,
        `- Ví dụ: "cơm trưa" → "Ăn uống", "xăng xe" → "Di chuyển", "tiền điện" → "Tiện ích", "mua sách" → "Giáo dục"`,
        `- Không cần giới hạn trong danh sách có sẵn, hãy đặt tên category phù hợp và mô tả`,
        '',
        'Trả về JSON array, mỗi dòng input thành 1 object trong array. Chỉ trả về JSON thuần, không có dấu ``` hoặc markdown:'
      ].join('\n');

      const response = await fetch(`https://aws-ai-worker.doannq01.workers.dev/ai/generate`, {
        // const response = await fetch(`http://localhost:8000/ai/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': tenantConfig.customApiKey
        },
        body: JSON.stringify({
          data: text,
          prompt: prompt
        })
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        if (response.status === 401) errorMessage = 'API key không hợp lệ';
        else if (response.status === 403) errorMessage = 'Không có quyền truy cập API';
        else if (response.status === 429) errorMessage = 'Vượt quá giới hạn API';
        else if (response.status >= 500) errorMessage = 'Lỗi server API';
        throw new Error(errorMessage);
      }

      const { data } = await response.json();
      const content = data?.content || data?.response || data?.answer || data?.result;

      if (!content) {
        throw new Error('Không nhận được phản hồi từ AI');
      }

      // Format lại JSON string
      const formattedContent = content
        .replace(/```json\n?/g, '')
        .replace(/```\n?/g, '')
        .replace(/\n/g, '')
        .replace(/\s+/g, ' ')
        .trim();

      const parsedData = JSON.parse(formattedContent);

      // Đảm bảo trả về array
      return Array.isArray(parsedData) ? parsedData : [parsedData];
    } catch (error) {
      console.error('Error analyzing with Gemini:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (!inputText.trim()) {
      setError('Vui lòng nhập thông tin giao dịch');
      return;
    }

    setIsAnalyzing(true);
    setError('');

    try {
      let analyzedTransactions;

      if (useAI) {
        // Sử dụng AI để phân tích
        analyzedTransactions = await analyzeWithGemini(inputText);
      } else {
        // Sử dụng parser thủ công
        analyzedTransactions = parseTransactions(inputText, type);
      }

      // Chuyển đổi thành Transaction array
      const transactions: Transaction[] = analyzedTransactions.map(analyzedData => ({
        date: analyzedData.date,
        title: analyzedData.title,
        category: analyzedData.category,
        type,
        amount: analyzedData.amount,
        rowIndex: 0 // This will be set when saved to Google Sheets
      }));

      // Gửi tất cả giao dịch lên server
      await onSubmit(transactions);

      // Reset form
      setInputText('');

    } catch (err) {
      console.error('Error:', err);
      setError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi phân tích dữ liệu');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, margin: '0 auto', mt: 2 }}>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom align="center">
            Nhập Giao Dịch
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Toggle Button for Income/Expense */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <ToggleButtonGroup
                value={type}
                exclusive
                onChange={(event, newType) => {
                  if (newType !== null) {
                    setType(newType);
                  }
                }}
                aria-label="Loại giao dịch"
              >
                <ToggleButton value="expense" aria-label="Chi">
                  Chi
                </ToggleButton>
                <ToggleButton value="income" aria-label="Thu">
                  Thu
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* AI Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useAI}
                    onChange={(e) => setUseAI(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      {useAI ? '🤖 Dùng AI' : '✍️ Nhập thủ công'}
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {/* Textarea for input */}
            <TextField
              label={`Nhập thông tin ${type === 'income' ? 'thu nhập' : 'chi tiêu'} (mỗi dòng một giao dịch)`}
              multiline
              rows={6}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              fullWidth
              placeholder={`Ví dụ:
Cơm trưa 50k
Xăng xe 200
Mua sách 150
Cà phê 25`}
              variant="outlined"
            />

            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              disabled={isLoading || isAnalyzing}
              sx={{ mt: 2 }}
            >
              {isLoading || isAnalyzing ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Phân Tích & Lưu'
              )}
            </Button>

            {/* Help text */}
            <Typography variant="body2" color="text.secondary" align="center">
              Hệ thống sẽ tự động phân tích và phân loại giao dịch của bạn
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Recommendations Section */}
      {getRecommendations().length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
              💡 Hôm nay bạn có thể có:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {getRecommendations().slice(0, 8).map((transaction, index) => (
                <Chip
                  key={index}
                  label={`${transaction.title} ${Math.round(transaction.amount / 1000)}k`}
                  onClick={() => handleRecommendationClick(transaction)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white'
                    },
                    transition: 'all 0.2s ease'
                  }}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Click vào các gợi ý để thêm vào ô nhập liệu
            </Typography>
          </CardContent>
        </Card>
      )}

      <MarketDataCard tenantConfig={tenantConfig} />
    </Box>
  );
};

export default TransactionInput; 